import { z } from 'zod';

import { extractedQuestionSchema } from '@/types/extractedQuestions';

/**
 * The canonical schema and picker predicate that define when the
 * ExtractedQuestionsBubble applies. The bubble keeps no client state: its
 * question cards are always shown, and card pulses run through Reanimated
 * SharedValues owned by the bubble's wiring hook.
 *
 * `extractedQuestionsAttributesSchema` defines the bubble's contribution
 * to message attributes; `messageAttributesSchema` composes it. The
 * `hasApprovedExtractedQuestions` predicate is the picker contract used
 * by `ChatContent`'s renderBubble to decide whether to instantiate this
 * bubble at all.
 */

/**
 * Bubble-specific message-attribute fields. Composed into the chat-wide
 * `messageAttributesSchema` so all bubble-related shape changes live here.
 */
export const extractedQuestionsAttributesSchema = z.object({
  extractedQuestions: z.array(extractedQuestionSchema).optional(),
  primaryQuestionIndex: z.number().optional(),
  /** Server-set guard. The pipeline writes extracted questions onto the
   * message immediately, but the bubble only renders once a reviewer has
   * approved them. Absence or `false` means "do not render". */
  approved: z.boolean().optional(),
});

export type ExtractedQuestionsAttributes = z.infer<typeof extractedQuestionsAttributesSchema>;

/**
 * Predicate for the ExtractedQuestionsBubble picker. The bubble renders
 * only when the pipeline-emitted questions have been approved by a
 * reviewer (`approved: true`) AND the array is non-empty. Missing
 * `approved` or `false` short-circuits to `false`.
 */
export function hasApprovedExtractedQuestions(
  message: ExtractedQuestionsAttributes | undefined
): boolean {
  if (!message?.approved) {
    return false;
  }

  return (message.extractedQuestions?.length ?? 0) > 0;
}
